import React from "react";
import { Box, Flex, Grid, GridItem, Text, useToken } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CheckboxFilterGroup, {
  CheckboxFilterOption,
} from "@/shared/components/filter/CheckboxFilterGroup";
import RadioFilterGroup from "@/shared/components/filter/RadioFilterGroup";
import FilterChipList, {
  FilterChipItem,
} from "@/shared/components/filter/FilterChipList";
import { buildFilterChips } from "@/shared/components/filter/buildFilterChips";
import PathPickFilterCountBadge from "@/features/pathpick/components/ui/PathPickFilterCountBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

export interface PathPickFilterGroup<K extends string> {
  key: K;
  title: string;
  options: CheckboxFilterOption[];
  // 지정 시 단일 선택(라디오) 섹션으로 그린다. allValue는 `미적용(전체)`을 뜻하는 센티널이라
  // 이 값이 선택된 상태는 getSelectedValues가 빈 배열로 내려주므로 배지/칩에서 자동 제외된다.
  single?: { allValue: string | number };
}

interface Props<K extends string> {
  isOpen: boolean;
  groups: PathPickFilterGroup<K>[];
  // 패널 안 체크 상태 — `필터 적용` 전까지는 드래프트 값이다.
  getSelectedValues: (key: K) => (string | number)[];
  // 칩·적용 개수 — 실제로 목록에 걸려 있는 적용값이다.
  getAppliedValues: (key: K) => (string | number)[];
  onToggle: (key: K, value: string | number) => void;
  onRemoveChip: (key: K, value: string | number) => void;
  onClearAll: () => void;
  onApply: () => void;
  onCancel: () => void;
  isApplyDisabled?: boolean;
  // 체크박스 그룹(배열형)으로 표현할 수 없는 커스텀 영역(범위 필터) — 그룹 카드 오른쪽에 넓게 붙는다.
  extraCells?: React.ReactNode;
  // extraCells의 칩. 적용 개수 배지에도 합산되므로 칩과 배지가 항상 일치한다.
  extraChips?: FilterChipItem[];
}

interface GroupCardProps<K extends string> {
  groupKey: K;
  title: string;
  options: CheckboxFilterOption[];
  // 지정 시 단일 선택(라디오) — 원시값으로 받아 memo 비교에서 객체 참조 문제가 없게 한다.
  singleAllValue?: string | number;
  selectedValues: (string | number)[];
  onToggle: (key: K, value: string | number) => void;
}

function GroupCard<K extends string>({
  groupKey,
  title,
  options,
  singleAllValue,
  selectedValues,
  onToggle,
}: GroupCardProps<K>) {
  const { t } = useI18nHelper();
  const handleToggle = (value: string | number) => onToggle(groupKey, value);

  return (
    <Flex
      flexDirection={"column"}
      gap={"8px"}
      padding={"10px"}
      minWidth={0}
      border={"0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      background={"#FFFFFF"}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <Text textStyle={"body-md-bold"} color={"#444440"} noOfLines={1}>
          {title}
        </Text>
        <PathPickFilterCountBadge
          label={t("path_pick:filter.selected_count", {
            count: selectedValues.length,
          })}
        />
      </Flex>
      {singleAllValue !== undefined ? (
        // 단일 선택 그룹은 값이 없으면(= 미적용) 전체 옵션이 선택된 것으로 보여준다.
        <RadioFilterGroup
          options={options}
          value={selectedValues[0] ?? singleAllValue}
          onChange={handleToggle}
        />
      ) : (
        <CheckboxFilterGroup
          options={options}
          selectedValues={selectedValues}
          onToggle={handleToggle}
          maxHeight={"200px"}
          searchPlaceholder={`${title} ${t("common:search")}`}
          noResultsText={t("common:no_results")}
        />
      )}
    </Flex>
  );
}

const isSameValues = (a: (string | number)[], b: (string | number)[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

// 그룹 카드 — 범위 입력처럼 다른 영역의 드래프트가 바뀔 때 브랜드 목록(옵션 수백~수천 개)까지
// 다시 그리지 않도록 memo로 끊는다. 선택값은 렌더마다 새 배열로 올 수 있어(품절 [값]) 요소로 비교하고,
// 나머지 props는 호출부가 고정 참조(useMemo/useCallback)로 넘긴다.
// 모바일 필터 모달(PathPickMobileFilterModal)도 같은 카드를 쓴다.
export const PathPickFilterGroupCard = React.memo(
  GroupCard,
  (prev, next) =>
    prev.groupKey === next.groupKey &&
    prev.title === next.title &&
    prev.options === next.options &&
    prev.singleAllValue === next.singleAllValue &&
    prev.onToggle === next.onToggle &&
    isSameValues(prev.selectedValues, next.selectedValues)
) as typeof GroupCard;

// 패스픽 필터 패널 셸 — 상품 찾기/담은 상품이 공유하는 프레젠테이셔널 컴포넌트.
// 필터 상태(적용값·드래프트)는 화면별 훅이 소유하므로 값 조회/변경은 전부 콜백으로 받는다.
// 패널 안의 변경은 하단 `필터 적용`을 눌러야 목록에 반영되고, 칩 삭제·전체 해제는 즉시 반영된다.
function PathPickFilterPanel<K extends string>({
  isOpen,
  groups,
  getSelectedValues,
  getAppliedValues,
  onToggle,
  onRemoveChip,
  onClearAll,
  onApply,
  onCancel,
  isApplyDisabled = false,
  extraCells,
  extraChips = [],
}: Props<K>) {
  const { t } = useI18nHelper();
  const [hairlineDefault] = useToken("shadows", ["hairlineDefault"]);

  const chips = [
    ...buildFilterChips({
      groups,
      getSelectedValues: (key) => getAppliedValues(key as K),
      onRemove: (key, value) => onRemoveChip(key as K, value),
    }),
    ...extraChips,
  ];

  const appliedCount = chips.length;

  // 패널이 접혀 있고 적용된 칩도 없으면 시각적으로 빈 상태이므로 DOM에서 제외해
  // 부모 flex gap이 높이 0짜리 래퍼에 이중으로 적용되는 것을 막는다.
  // (null을 반환해도 컴포넌트는 마운트 상태라 suspense 쿼리/draft는 유지된다)
  if (!isOpen && chips.length === 0) {
    return null;
  }

  return (
    <Flex flexDirection={"column"} gap={"8px"}>
      {/* 패널 본문만 접고, 적용된 필터 칩은 접힘 여부와 무관하게 항상 노출한다. */}
      <Box display={isOpen ? "block" : "none"}>
        <Flex
          flexDirection={"column"}
          gap={"12px"}
          boxShadow={hairlineDefault}
          padding={"12px"}
          borderRadius={"8px"}
          background={"#F7F9FA"}
        >
          <Flex gap={"8px"} alignItems={"center"}>
            <Text textStyle={"body-md-bold"} color={"#444440"}>
              {t("path_pick:filter.title")}
            </Text>
            {appliedCount > 0 && (
              <PathPickFilterCountBadge
                label={t("path_pick:filter.applied_count", {
                  count: appliedCount,
                })}
              />
            )}
          </Flex>
          {/* 그룹 카드와 범위 영역을 한 줄에 모두 두고, 범위 영역만 넓게 잡는다.
              모바일처럼 좁은 폭에서는 들어갈 자리가 없어 한 열로 쌓는다. */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: `repeat(${groups.length}, minmax(0, 1fr))${
                extraCells ? " minmax(0, 2.4fr)" : ""
              }`,
            }}
            gap={"12px"}
          >
            {groups.map((group) => (
              <PathPickFilterGroupCard
                key={group.key}
                groupKey={group.key}
                title={group.title}
                options={group.options}
                singleAllValue={group.single?.allValue}
                selectedValues={getSelectedValues(group.key)}
                onToggle={onToggle}
              />
            ))}
            {extraCells && (
              <GridItem gridColumn={{ base: "1 / -1", md: "auto" }}>
                {extraCells}
              </GridItem>
            )}
          </Grid>
          <Flex justifyContent={"flex-end"} gap={"8px"}>
            <AppButton variant={"white"} onClick={onCancel}>
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:filter.cancel")}
              </Text>
            </AppButton>
            <AppButton
              variant={"primary"}
              onClick={onApply}
              isDisabled={isApplyDisabled}
            >
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:filter.apply")}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      </Box>
      <FilterChipList
        title={t("path_pick:filter.applied")}
        chips={chips}
        clearAllLabel={t("path_pick:filter.clear_all")}
        onClearAll={onClearAll}
      />
    </Flex>
  );
}

export default PathPickFilterPanel;
